/**
 * ErrorBoundary — last-resort render guard.
 *
 * React has no hook equivalent for `componentDidCatch`, so this stays a
 * class component. It catches synchronous render/lifecycle throws from
 * its subtree and swaps in an on-brand GlassPanel fallback instead of
 * letting the whole app blank out.
 *
 * Two layers use it (see main.tsx / App.tsx):
 *   - a top-level `section="app"` boundary around the entire app, the
 *     last line of defence for chrome/nav/palette crashes;
 *   - a per-view boundary keyed on the active view, so a crash in one
 *     surface is contained and the menu + command palette survive.
 *
 * It does NOT catch async errors (event handlers, subscriptions,
 * promises) — those are surfaced separately through the storeHealth bus
 * + ToastProvider. This guards the render path only.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { GlassPanel } from './GlassPanel';

interface ErrorBoundaryProps {
  /** Human-readable area name, used in the message + the console tag. */
  section?: string;
  /** Called when the user clicks "Try again" (after resetting state). */
  onReset?: () => void;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const tag = this.props.section ? `[boundary:${this.props.section}]` : '[boundary]';
    // Keep the trail in the console for diagnosis. (Telemetry hook can
    // go here later — intentionally just a log for now.)
    console.error(tag, error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const where = this.props.section ? `the ${this.props.section} view` : 'this area';

    return (
      <div className="error-boundary">
        <GlassPanel variant="default" className="error-boundary__panel">
          <span className="t-eyebrow">Something went wrong</span>
          <h2 className="error-boundary__title">
            {where.charAt(0).toUpperCase() + where.slice(1)} hit an error.
          </h2>
          <p className="error-boundary__body">
            The rest of Argo OS is still running. You can retry this view or
            reload the app.
          </p>
          {import.meta.env.DEV && (
            <pre className="error-boundary__detail">{error.message}</pre>
          )}
          <div className="error-boundary__actions">
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={this.handleReset}
            >
              Try again
            </button>
            <button
              type="button"
              className="glass-btn"
              onClick={this.handleReload}
            >
              Reload app
            </button>
          </div>
        </GlassPanel>
      </div>
    );
  }
}
